import { CoreExecutorType } from '@/common/constants/core'
import { IKey } from '@/common/constants/ikey'
import { isMainFramePageData, STARTUP_ACTION_FLAG } from '@/common/utils/mainFrameSlot'
import {
  clearMainFrameRestoreSnapshot,
  isRestorableMainFrameFormId,
  persistMainFrameRestoreSnapshot,
  readMainFrameRestoreSnapshot,
} from '@/common/utils/mainFrameRestore'
import { resolveStartupActionWindowType } from '@/common/utils/resolveStartupActionWindowType'
import { normalizeDialogPageUniDataFieldAliases } from '@/common/utils/cloneTableValue'
import { areAllLayoutPanelsHiddenByStates } from '@/common/utils/startupDialogPanelVisibility'
import { shouldRunStartupDialogServerOnLoadCatchUp } from '@/common/utils/startupDialogOnLoadCatchUp'
import { seedPlatformOpenPageUniData } from '@/common/utils/seedPlatformOpenPageUniData'
import { buildDialogCloseConfigForStore } from '@/common/utils/dialogCloseParentMergeScope'
import { createPlatformActionParams } from '@/common/utils/requestSanitizer'
import apiService from '@/services/api.service'
import { markFormPageBootstrap } from '@/common/utils/formPageBootstrap'
import { createSafeClientData } from '@/services/core/core.service'
import {
  applyActionResponseFormLayout,
  buildMainFramePageDataFromActionResponse,
  MAIN_FRAME_SLOT_ID,
  resolveMainFrameFormId,
} from '@/services/mainFrame/mainFrameMount'
import type { useAppStore } from '@/stores/app'
import { runStartupServerRootOnLoadIfNeeded } from '@/services/startup/runStartupServerRootOnLoad'

export const STARTUP_ACTION_DIALOG_FLAG = STARTUP_ACTION_FLAG

export type StartupActionExecuteResult = 'main_frame' | 'dialog' | false

export type PendingStartupAction = {
  id: number
  windowType?: string
  addedDate?: string
}

export type ExecuteStartupActionOptions = {
  actionId?: number
  /** Из pending startup API — влияет на deferServerOnLoad (только MAIN_FRAME откладывает onLoad). */
  windowType?: string
}

/** Middle onLoad при open откладывается только для startup MAIN_FRAME (см. ServerOnLoadExecutionPolicy). */
export function shouldDeferServerOnLoadForStartup(windowType?: string): boolean {
  const normalized = String(windowType ?? '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
  return normalized === 'MAIN_FRAME'
}

const SESSION_RAN_KEY = 'startup-actions-ran'
const MAX_STARTUP_CHAIN_STEPS = 16

type AppStore = ReturnType<typeof useAppStore>

let chainInProgress = false

export function resetStartupActionSession(): void {
  try {
    sessionStorage.removeItem(SESSION_RAN_KEY)
  } catch {
    // ignore
  }
  clearMainFrameRestoreSnapshot()
}

export function markStartupActionsRanForSession(): void {
  try {
    sessionStorage.setItem(SESSION_RAN_KEY, '1')
  } catch {
    // ignore
  }
}

export function hasStartupActionsRanForSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_RAN_KEY) === '1'
  } catch {
    return false
  }
}

export async function fetchPendingStartupActions(): Promise<PendingStartupAction[]> {
  try {
    const data = await apiService.platformStartupActionsPending()
    const actions = Array.isArray(data?.actions) ? data.actions : []
    const normalized: PendingStartupAction[] = []
    for (const item of actions) {
      const id = Number((item as PendingStartupAction)?.id)
      if (!Number.isFinite(id) || id <= 0) continue
      const windowTypeRaw = (item as PendingStartupAction)?.windowType
      normalized.push({
        id,
        windowType: windowTypeRaw != null ? String(windowTypeRaw).trim() : undefined,
        addedDate: (item as PendingStartupAction)?.addedDate,
      })
    }
    return normalized
  } catch (error) {
    console.warn('[StartupAction] pending list failed', error)
    return []
  }
}

export async function hasPendingStartupActions(): Promise<boolean> {
  try {
    const data = await apiService.platformStartupActionsPending()
    if (Array.isArray(data?.actions) && data.actions.length > 0) {
      return true
    }
    return data?.pending === true
  } catch (error) {
    console.warn('[StartupAction] pending probe failed', error)
    return false
  }
}

function resolveStartupDialogFormId(response: Record<string, unknown>): string {
  return resolveMainFrameFormId(response)
}

function isStartupActionResponseOpen(response: Record<string, unknown>): boolean {
  const status = Number(response.status)
  if (!Number.isFinite(status) || status < 1) {
    return false
  }
  const form = response.form
  if (form == null) {
    return false
  }
  if (typeof form === 'string') {
    return form.trim().length > 0
  }
  return typeof form === 'object'
}

function resolveStartupWindowType(documentData: Record<string, unknown>): string {
  return resolveStartupActionWindowType(documentData)
}

function isMainFramePresentInStore(appStore: AppStore): boolean {
  return isMainFramePageData(appStore.pagesData?.[MAIN_FRAME_SLOT_ID])
}

async function navigateToHomeIfNeeded(): Promise<void> {
  try {
    const { default: router } = await import('@/router')
    if (router.currentRoute.value.name !== 'home') {
      await router.push({ name: 'home' })
    }
  } catch (error) {
    console.warn('[StartupAction] Failed to navigate to home for MAIN_FRAME', error)
  }
}

function resolveStartupActionId(options?: ExecuteStartupActionOptions): number {
  const raw = options?.actionId
  if (raw == null) return -1
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : -1
}

export async function executeNextStartupAction(
  appStore: AppStore,
  options?: ExecuteStartupActionOptions,
): Promise<StartupActionExecuteResult> {
  const actionId = resolveStartupActionId(options)
  const deferServerOnLoad = shouldDeferServerOnLoadForStartup(options?.windowType)
  const payload = createPlatformActionParams({
    actionId,
    actionType: 'SERVICE_ACTION',
    type: CoreExecutorType.WF,
    sessionId: null,
    clientData: createSafeClientData({
      fields: {
        fields: {
          [IKey.PARAM_CLIENT_TYPE]: {
            type: 'String',
            value: IKey.CLIENT_WEB,
          },
        },
      },
    }),
    formState: { params: {} },
    openingParams: {
      newWindow: false,
      ignoreExistingStack: true,
      tempFid: null,
      deferServerOnLoad,
    },
  })

  const httpResponse = await apiService.platformAction(payload)
  const documentData = (httpResponse?.data ?? httpResponse) as Record<string, unknown>

  if (!isStartupActionResponseOpen(documentData)) {
    return false
  }

  const windowType = resolveStartupWindowType(documentData)
  if (windowType === 'MAIN_FRAME') {
    if (isMainFramePresentInStore(appStore)) {
      console.warn('[StartupAction] Ignoring duplicate MAIN_FRAME startup response')
      return 'main_frame'
    }
    const openedFormId = resolveMainFrameFormId(documentData)
    const prepared = await buildMainFramePageDataFromActionResponse(documentData, {
      isStartupAction: true,
      formIdHint: openedFormId,
      appStore,
    })
    appStore.setPageData(MAIN_FRAME_SLOT_ID, prepared, null)
    if (isRestorableMainFrameFormId(openedFormId)) {
      persistMainFrameRestoreSnapshot({
        sessionId: String(prepared.sessionId ?? documentData.sessionId ?? ''),
        formId: openedFormId,
      })
    }
    await navigateToHomeIfNeeded()
    return 'main_frame'
  }

  const formId = resolveStartupDialogFormId(documentData)
  await applyActionResponseFormLayout(documentData, formId)

  seedPlatformOpenPageUniData(documentData)

  const dialogCloseConfig = buildDialogCloseConfigForStore(
    {},
    {
      pageDataSlot: null,
      onCloseDialogParentSlotId: null,
    },
  )
  dialogCloseConfig[STARTUP_ACTION_DIALOG_FLAG] = true

  normalizeDialogPageUniDataFieldAliases(documentData)
  documentData.$isDialog = true
  documentData[STARTUP_ACTION_DIALOG_FLAG] = true

  appStore.addDialog({
    id: formId,
    sessionId: String(documentData.sessionId ?? ''),
    config: dialogCloseConfig,
    data: documentData,
  })
  const { ensureWebClientOnLoadBeforeOpenForm } = await import(
    '@/services/action/webClientFormOnLoadBeforeBuild'
  )
  const preparedDocumentData = await ensureWebClientOnLoadBeforeOpenForm(documentData, {
    appStore,
    openedFormId: formId,
    parseForm: false,
  })
  appStore.setPageData(formId, preparedDocumentData)
  const panelsHiddenByStates = areAllLayoutPanelsHiddenByStates(
    documentData.form,
    (documentData.jsResult as { states?: unknown } | undefined)?.states,
  )
  const needsServerOnLoadCatchUp = shouldRunStartupDialogServerOnLoadCatchUp(documentData)
  if (deferServerOnLoad || panelsHiddenByStates || needsServerOnLoadCatchUp) {
    await runStartupServerRootOnLoadIfNeeded(appStore, formId, documentData)
  }
  markFormPageBootstrap(formId)

  if (import.meta.env.DEV && panelsHiddenByStates) {
    const fresh = appStore.pagesData?.[formId] as Record<string, unknown> | undefined
    if (
      areAllLayoutPanelsHiddenByStates(
        fresh?.form ?? documentData.form,
        (fresh?.jsResult as { states?: unknown } | undefined)?.states ??
          (documentData.jsResult as { states?: unknown } | undefined)?.states,
      )
    ) {
      console.warn(
        '[StartupAction] Main panels still hidden by jsResult.states after onLoad catch-up — check middle onLoad / visibility states.',
      )
    }
  }

  return 'dialog'
}

async function tryRestoreMainFrameFromSession(appStore: AppStore): Promise<boolean> {
  if (isMainFramePresentInStore(appStore)) {
    return true
  }
  const snapshot = readMainFrameRestoreSnapshot()
  if (!snapshot) {
    return false
  }

  try {
    appStore.setPageData(snapshot.formId, { sessionId: snapshot.sessionId }, null)
    const loaded = await appStore.getPage({
      id: snapshot.formId,
      platform: CoreExecutorType.WF,
      fields: {},
      parentPageId: null,
    })
    if (loaded == null || typeof loaded !== 'object' || !(loaded as Record<string, unknown>).form) {
      return false
    }

    const prepared = await buildMainFramePageDataFromActionResponse(loaded as Record<string, unknown>, {
      isStartupAction: true,
      formIdHint: snapshot.formId,
      appStore,
    })
    appStore.setPageData(MAIN_FRAME_SLOT_ID, prepared, null)
    if (
      snapshot.formId !== MAIN_FRAME_SLOT_ID &&
      appStore.pagesData?.[snapshot.formId]
    ) {
      appStore.removePage(snapshot.formId, { skipStartupReschedule: true })
    }
    await navigateToHomeIfNeeded()
    return true
  } catch (error) {
    console.warn('[StartupAction] main frame restore failed', error)
    clearMainFrameRestoreSnapshot()
    return false
  }
}

async function runStartupActionChain(appStore: AppStore): Promise<boolean> {
  if (chainInProgress) {
    return false
  }
  chainInProgress = true
  try {
    const pendingActions = await fetchPendingStartupActions()
    let openedAny = false

    if (pendingActions.length > 0) {
      const steps = pendingActions.slice(0, MAX_STARTUP_CHAIN_STEPS)
      for (const pending of steps) {
        const result = await executeNextStartupAction(appStore, {
          actionId: pending.id,
          windowType: pending.windowType,
        })
        if (!result) {
          break
        }
        openedAny = true
        if (result === 'dialog') {
          break
        }
      }
    } else if (await hasPendingStartupActions()) {
      const result = await executeNextStartupAction(appStore)
      if (result) {
        openedAny = true
      }
    }

    if (!openedAny) {
      if (!isMainFramePresentInStore(appStore)) {
        return await tryRestoreMainFrameFromSession(appStore)
      }
      return isMainFramePresentInStore(appStore)
    }
    return true
  } catch (error) {
    console.error('[StartupAction] Error while performing startup action', error)
    return false
  } finally {
    chainInProgress = false
  }
}

export async function runStartupActions(appStore: AppStore): Promise<void> {
  const mainFramePresent = isMainFramePresentInStore(appStore)
  if (hasStartupActionsRanForSession() && mainFramePresent) {
    if (!(await hasPendingStartupActions())) {
      return
    }
  }
  const succeeded = await runStartupActionChain(appStore)
  if (succeeded) {
    markStartupActionsRanForSession()
  }
}

export function scheduleNextStartupActionAfterClose(appStore?: AppStore): void {
  queueMicrotask(() => {
    void (async () => {
      const store = appStore ?? (await import('@/stores/app')).useAppStore()
      await runStartupActionChain(store)
    })()
  })
}

export const startupActionService = {
  hasPendingStartupActions,
  fetchPendingStartupActions,
  executeNextStartupAction,
  runStartupActions,
  scheduleNextStartupActionAfterClose,
  resetStartupActionSession,
  markStartupActionsRanForSession,
  hasStartupActionsRanForSession,
}

export default startupActionService
